import {
  joinMembershipPath,
  membershipStatusPage,
  onboardingQuizPath,
  searchPath,
} from "@outschool/routes";
import { useSession } from "@outschool/ui-auth";
import React from "react";
import { useLocation } from "react-router";

import { usePaymentMethods } from "../../hooks/CurrentUser";
import { useBannerDismissal } from "../../hooks/useBannerDismissal";
import { useCreditOffer } from "../../hooks/useCreditOffer";
import { useMembershipRequirement } from "../subscriptions/hooks/useMembershipRequirement";
import BackupPaymentBanner from "./BackupPaymentBanner";
import CreditOfferBanner from "./CreditOfferBanner";
import FirstMonthFreeBanner from "./FirstMonthFreeBanner";
import MusicShowcaseBanner, {
  IsValidTimeRangeForMusicShowcaseBanner,
} from "./MusicShowcaseBanner";
import ReferralSignupBanner from "./ReferralSignupBanner";

// Use this for running experiments and diverting user cohorts to different membership banners.
export default function MembershipsBannerSwitcher() {
  const { isLoggedIn } = useSession();
  const { creditOffer } = useCreditOffer();
  const location = useLocation();
  const { shouldShowBannersToday, isBannerDismissed } = useBannerDismissal();
  const { mustPurchaseMembership } = useMembershipRequirement();
  const { paymentMethods, loading: paymentMethodsLoading } =
    usePaymentMethods();

  // Global rules: Don't show any banners on these pages
  const shouldShowBannerByPath = React.useMemo(() => {
    if (
      location.pathname.includes(onboardingQuizPath()) ||
      location.pathname.includes(searchPath()) ||
      location.pathname.includes("/users/") ||
      location.pathname.includes("/membership/subscribe/") ||
      location.pathname.includes(membershipStatusPage()) ||
      location.pathname === joinMembershipPath() ||
      location.pathname.includes("/teach/") ||
      location.pathname.includes("/account/teacher") ||
      location.pathname.includes("/class-comparison")
    ) {
      return false;
    }

    return true;
  }, [location.pathname]);

  const firstMonthFreeUsers = !isLoggedIn || mustPurchaseMembership;

  const musicShowcaseDismissed = isBannerDismissed("music_showcase");

  const hasBackupPaymentMethod = React.useMemo(() => {
    return paymentMethods.some(
      paymentMethod => !!paymentMethod.isBackUpForOffSessionCharges,
    );
  }, [paymentMethods]);

  const shouldShowBackupBanner =
    isLoggedIn &&
    !paymentMethodsLoading &&
    paymentMethods.length > 0 &&
    !hasBackupPaymentMethod;

  // If a banner was dismissed today, don't show any banners
  if (!shouldShowBannersToday || !shouldShowBannerByPath) {
    return null;
  }

  if (!musicShowcaseDismissed && IsValidTimeRangeForMusicShowcaseBanner()) {
    return <MusicShowcaseBanner />;
  } else if (isLoggedIn && creditOffer) {
    return <CreditOfferBanner />;
  } else if (firstMonthFreeUsers) {
    return <FirstMonthFreeBanner />;
  } else if (shouldShowBackupBanner) {
    return <BackupPaymentBanner />;
  } else if (isLoggedIn) {
    return <ReferralSignupBanner />;
  } else {
    return null;
  }
}
